<template>
  <div class="help-content" v-loading="loading">
    <div v-if="error" class="error-message">
      <el-empty description="도움말을 불러올 수 없습니다." :image-size="80" />
    </div>
    <!-- eslint-disable-next-line vue/no-v-html -- DOMPurify로 새니타이징된 값만 바인딩됨 (utils/sanitizeHtml.js) -->
    <div v-else class="markdown-content" v-html="sanitizeHtml(renderedContent)"></div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

const props = defineProps({
  contentPath: {
    type: String,
    required: true
  }
})

const loading = ref(false)
const error = ref(false)
const content = ref('')

// 내장 도움말 콘텐츠
const helpContents = {
  'screens/schedule-board': `
## 스케줄 보드 (상황판)

스타일 × T&A 16단계 진행 현황판입니다. 샘플 단계(QC샘플)부터 한국입고까지
"어느 스타일이 어디까지 왔나"를 브랜드 관점으로 봅니다.

- **행**: 스타일이 색상(차수)별 줄로 나뉩니다 — DG#2 등은 그 색상의 재발주 차수.
- **현황 열**: 첫 미완료 단계 + 지연 n일(빨강)/D-n + 최신 비고 한 줄.
- **지연만 보기**: 문제 오더만 필터. 지연 큰 순으로 정렬됩니다.
- **칸 클릭 → 팝업**: 그 단계의 마일스톤(예정/실제/상태) 수정, [오늘 완료],
  **관련 산출물**(근거 문서 목록·열기), 증빙 첨부(아트웍 도안·촬영/수납 사진).
  QC샘플 칸은 치수 대조표에서 실측 입력 → [QC 컨펌 완료].
- 대부분의 단계는 산출물 등록 시 **자동 완료**됩니다 (발주서→원단발주,
  생산지시서 발행→최종작지, 완제품입고→한국입고 등).
- **[브랜드 전달 엑셀]**: 최신 주간 스케줄 서식에 시스템 날짜를 덮어쓴
  갱신본을 생성 — 변경 내역을 확인한 뒤 다운로드해 브랜드에 전달합니다.
  `,
  'screens/milestone-template': `
## 마일스톤 템플릿 (기준정보)

메인오더 생성 시 자동으로 깔리는 T&A 단계를 브랜드별로 관리합니다.

- 적용 순서: **브랜드 템플릿 → 전사 공용 → 시스템 기본 16단계**.
- [기본 16단계 불러오기]로 시작해 D-day(납기 역산)·담당·알림일을
  브랜드 관행에 맞게 조정하세요.
- 단계명은 표준 이름을 유지해야 스케줄 보드·자동완료와 연동됩니다.
- 템플릿 수정은 **신규 메인오더부터** 적용 — 기존 오더는 바뀌지 않습니다.
  `,
  'screens/quotation-list': `
## 견적관리

사전원가(PC)와 견적서(QT)를 관리합니다 — 같은 데이터 구조의 두 유형입니다.

### 흐름
1. **사전원가 작성**: 원가 구조를 먼저 잡는 내부 검토용 (견적서로 전환 가능)
2. **견적서 작성**: 원부자재비 → 임가공비 → 경비 순으로 입력하면 제조원가·견적가 자동 계산
3. **확정**: 확정(잠금) 후에는 합의가(NEGO)·메모만 수정 가능
4. **리비전 생성**: 협상으로 조건이 바뀌면 새 버전 생성 (이전 버전은 '대체됨')
5. **메인오더 생성**: 확정된 견적서에서 생성 — 생산 단계로 연결

### 참고
- 단가·마진 등 민감 정보는 견적/원가 권한 그룹만 표시됩니다.
- 원가 시뮬레이션: 상세 화면에서 환율·단가 변동 시나리오를 미리 계산할 수 있습니다.
  `,
  'screens/cost-comparison': `
## 원가 비교 분석

메인오더별로 **사전원가(견적 제조원가) vs 실제원가**를 비교합니다.

### 데이터 원천
- 사전원가: 연결된 견적서의 제조원가 (마진 제외)
- 실제원가: 지출결의(입고 연결·작업지시 배분) + LOT 배분 + 수입송장 + 노무비
- 납품가: 합의가(NEGO) > 확정단가 > PCS단가 순 — 마진 분석의 매출 기준

### 사용법
- [분석 생성/갱신]: 지결·송장 입력 후 다시 눌러 최신 실제원가 반영
- [전체 분석 갱신]: 전 메인오더 일괄 갱신 (배포·마감 후 권장)
- 차이율 ±5% 초과 항목이 견적 정확도 개선의 입력이 됩니다.
  `,
  'screens/material-order-list': `
## 발주서관리

원부자재 발주의 시작점입니다. 발주 → 입고 → 지출결의 → 원가 집계로 이어집니다.

### 흐름
1. 발주서 작성 (메인오더·자재 마스터 연결, 품목·수량·단가)
2. 결재 상신 → 승인 시 자동 '발주완료'
3. 입고 등록 → 입고 현황에 따라 상태 자동 갱신 (일부입고/입고완료)
4. 업체 정산 또는 지결 작성에서 입고내역 연결 → 스타일 원가 집계

### 주의
- 지결에 연결된 입고는 수정·삭제가 잠깁니다 (정산 근거 보존)
- 발주서는 월 매입 보고의 원천입니다 (발주일 기준)
  `,
  'screens/receiving-list': `
## 입고 목록

발주 품목의 입고 내역을 등록·관리합니다.

### 사용법
- 입고 등록 시 발주 품목·입고일·수량·창고를 기록합니다.
- 입고 수량에 따라 발주서 상태가 자동 갱신됩니다.
- 지결 연결 여부(정산 상태)를 필터로 확인할 수 있습니다.

### 주의
- 지출결의서에 연결된 입고는 수정·삭제할 수 없습니다 — 해당 지결에서
  연결을 해제한 뒤 처리하세요.
  `,
  'screens/design-list': `
## 디자인 관리

스타일(디자인)의 마스터 목록입니다. 모든 업무(샘플·견적·생산·원가)의 출발점.

### 주요 기능
- 스타일 등록: 내부번호(EL-YYYYSS-NNN) 자동 채번, 성별·제품종류·스타일 코드로 품명 자동 생성
- 라이프사이클 표시: 샘플·견적·작업지시 진행 건수를 한눈에
- 엑셀 일괄 등록/다운로드, 복사(파생 스타일)
- 상세: 마카·요척·다운스펙(디자인 속성) → 샘플·견적·생산(진행 이력) 탭 순서

### 참고
- 시즌 단위 현황은 '시즌 현황' 화면에서 (샘플비·패턴비 개발비 집계 포함)
  `,
  'screens/customer-list': `
## 고객사/브랜드 관리

고객사(법인)와 브랜드 마스터를 관리합니다.

- 브랜드는 디자인·견적·작업지시·매출의 축입니다.
- 담당 법인 변경은 '담당법인 변경' 단일 경로로만 (매출처관리자 권한).
- 브랜드 기반 접근 제어: 사용자에게 배정된 브랜드의 데이터만 보입니다.
  `,
  'screens/brand-assignment': `
## 브랜드 담당자 관리

사용자별로 담당 브랜드를 배정합니다.

- 배정된 브랜드의 디자인·견적·작업지시만 조회/작성할 수 있습니다 (접근 제어).
- 담당 브랜드가 없는 계정은 관련 화면에서 데이터가 보이지 않으니,
  신규 계정 세팅 시 반드시 배정하세요.
  `,
  'screens/delivery-list': `
## 납품/반품 관리

완제품의 고객사 납품과 반품을 관리합니다. (**제품** 메뉴 — 완제품 입고 다음 단계)

- 납품 등록: 메인오더·수량·납품처·예정일/실제일
- 납품 완료 후 본사에서 거래명세표가 오면 **'월 매출·매입' 화면에서
  거래명세표를 등록**하세요 — 그것이 매출 인식(명세일 기준)입니다.
- 반품은 별도 등록해 수량을 관리합니다.
  `,
  'screens/inbound-list': `
## 입고 관리

자재 입고(자재 메뉴)와 완제품 입고(제품 메뉴)가 같은 화면을 씁니다 —
어느 메뉴로 들어왔는지에 따라 유형이 자동 고정됩니다 (2026-08-02).

- **자재 입고**: 창고 자재 입고. 등록 시 '원단도착' 마일스톤이 자동 반영됩니다.
- **완제품 입고**: 완제품 한국 도착. 등록 시 '한국입고' 마일스톤이
  수량 커버리지로 자동 반영됩니다 (분할 입고 = "진행 n/m").
- 입고 후 납품(제품 > 납품/반품) 단계로 이어집니다.
  `,
  'screens/outbound-list': `
## 출고 관리

본사 창고에서의 출고(납품 출고 등)를 관리합니다.

- 출고 유형·수량·일자를 기록하며 재고 흐름의 근거가 됩니다.
  `,
  'screens/material-transfer-list': `
## 자재전달 현황

입고된 원부자재를 생산 공장으로 보내는 전달(출고) 현황입니다.

- 메인오더·자재별 전달 수량을 기록해 "입고됐지만 공장 미전달" 자재를 추적합니다.
- 패킹리스트·선적과 연계되어 수출 자재의 흐름을 구성합니다.
  `,
  'screens/packing-list': `
## 패킹리스트 관리

패킹리스트는 **대상**에 따라 두 종류입니다 (2026-08-02).

- **자재 패킹리스트** (자재 메뉴): 한국→공장으로 보내는 원부자재. 입고 내역을
  카톤에 배분하고, 혼적·카톤 범위를 지원하며 수출신고와 연결됩니다.
- **완제품 패킹리스트** (제품 메뉴): 공장→한국 완제품 선적(ACTUAL PKL).
  카톤별로 메인오더·색상·사이즈·개입수를 입력하고(혼적 오더 가능),
  스타일별 정품/발주 커버리지가 표시됩니다. 확정 후 ACTUAL PKL 양식 엑셀 출력.
- 메뉴로 진입하면 대상이 자동 고정됩니다 — 교차 생성 방지.
  `,
  'screens/inspection-list': `
## 검품 관리

생산 제품의 검품 기록을 관리합니다.

- 메인오더별 검품 일자·수량·결과(합격/불합격)·불량 내용을 기록합니다.
- 검품 비용은 지출결의 비용구분(검품/검사)으로 기표하면 원가에 집계됩니다.
  `,
  'screens/test-report-list': `
## 시험성적서 관리

원단·제품의 시험성적서(FITI·KATRI 등)를 관리합니다.

- 시험 의뢰·결과·성적서 파일을 스타일과 연결해 보관합니다.
- 시험비는 지출결의 비용구분(시험/성적)으로 기표하면 검사비로 집계됩니다.
  `,
  'screens/import-invoice-list': `
## 수입송장 관리

해외 공장의 수입송장(Invoice)을 관리합니다 — **임가공비(CMT)의 원천**.

- CMT 라인($ 단가 × 수량 × 환율)과 부대비용(통관·운임)을 등록하면
  해당 메인오더의 실제 임가공비로 집계됩니다.
- **부대비 청구처**(관세사/포워더 매입처)를 연결하면 그 업체의 세금계산서가
  왔을 때 업체 정산에서 송장 단위로 자동 대조됩니다 (지결은 참고 기표 —
  원가는 이 송장이 정본).
- 주의: 수입 대금·운임·관세를 지결로 배분하면 이중 계상 — 크로스체크가 경고합니다.
- LC·유산스와 연결되어 무역 금융 비용도 함께 추적됩니다.
  `,
  'screens/freight-cost-list': `
## 운임비용 (레거시 — 수입송장으로 일원화)

이 화면은 폐지되었습니다 (2026-07-21). **운임의 정본은 수입송장 부대비**
(운임·내륙운송 필드)이며, 원가는 무역 수집기가 송장에서 직접 집계합니다.
기존 입력 데이터는 보존됩니다.
  `,
  'screens/lg-list': `
## LG(보증서) 관리

수입 화물 선취를 위한 LG(Letter of Guarantee) 발급·회수를 관리합니다.

- LC·선적 건과 연결해 발급일·회수일·상태를 추적합니다.
  `,
  'screens/executive-dashboard': `
## 경영 현황

경영진용 요약 대시보드입니다.

- 매출·원가·마진, 진행 오더, 자금 현황 등 핵심 지표를 모아 보여줍니다.
- 각 카드에서 해당 상세 화면으로 이동할 수 있습니다.
- 월 매출·매입 보고는 지출·자금 > 월 매출·매입에서 확인하세요.
  `,
  'screens/audit-log': `
## 감사 추적 (Audit Trail)

주요 데이터의 생성·수정·삭제 이력을 기록합니다.

- 누가·언제·무엇을 바꿨는지 필드 단위 변경 내역을 남깁니다.
- 민감 화면(원가·지결) 조회 이력도 기록됩니다.
- 정산·마감 후 정정 추적의 근거로 사용하세요.
  `,
  'screens/quotation-detail': `
## 견적서 상세

원부자재비 → 임가공비 → 경비 순으로 입력하면 제조원가·견적가가 자동 계산됩니다.

### 주요 기능
- **원부자재비**: 수동 추가 또는 가져오기(요척/샘플/단가표). 'CMT포함' 스위치 =
  브랜드 제공(사급) 자재 — 켜면 우리 부담 원가에서 제외됩니다.
- **임가공(CMT)**: 공장(협력업체) 선택 시 생산국 자동, $단가×환율×수량
- **원가 시뮬레이션**: 환율·단가·마진 변동 시나리오를 저장 없이 계산
- **확정**: 잠금 — 이후 합의가(NEGO)·메모만 수정 가능
- **리비전 생성**: 조건 변경 시 새 버전 (이전 버전은 '대체됨' 보존)
- **메인오더 생성**: 확정 견적에서 생산 단계로 연결

### 주의
- 사전원가(PC)와 견적서(QT)는 같은 화면을 씁니다 — 상단 유형 태그 확인
- 합의가는 PCS 단가 기준으로 입력하세요 (총액 아님)
  `,
  'screens/sample-cost': `
## 샘플비/패턴비 원장

디자인실 정산 엑셀(사내샘플실/외주샘플/패턴)의 시스템 대체 — 개발비의 공식 원천.

### 입력
- 스타일번호는 검색해 선택하면 품명·시즌 자동 채움 (미등록 과거 스타일은 직접 입력)
- 사내: 기본가격+부속추가 = FIX 단가, 지급단가 = FIX×50% 자동
- 외주: 단가×수량. **샘플 작업 내역에서 외주 작업 완료 시 자동 기표**됩니다.

### 집계·정산
- 디자인 연결된 행이 시즌 현황 개발비(샘플/패턴)로 집계
- 월마감 탭: 구분별 합계 + 사내 작업자별 지급 명세 + 외주 업체별 소계
- **지결번호가 기입된 행(업체 정산 완료)은 수정·삭제가 잠깁니다**
- '스타일 미연결만' 체크로 집계 누락 행을 찾아 [수정]→저장으로 재연결하세요
  `,
  'screens/season-pipeline': `
## 시즌 현황

시즌(연도+SS/FW)별 스타일 파이프라인 — 샘플 진행 → 오더 확정 → 드랍.

### 카드 정보
- 개발비(원장 집계): 샘플비·패턴비 — 샘플/패턴비 원장에서 디자인 연결 행의 합
- 샘플요청 진행 상태, 오더(작업지시) 수량·납기, 소싱 후보
- 드랍 처리 시 사유 기록 — 시즌 요약에 드랍 개발비(매몰 비용) 집계

### 참고
- 개발비가 0이면 원장 행이 스타일 미연결 상태일 수 있습니다 —
  원장 화면에서 '스타일 미연결만'을 확인하세요.
- 재단·봉제 등 메인 생산 공정비는 여기 아닌 메인오더 원가에 집계됩니다.
  `,
  'screens/design-detail': `
## 디자인 상세

스타일의 모든 정보 — 디자인 속성(마카·요척·다운스펙)이 앞, 진행 이력(샘플·견적·생산)이 뒤.

### 탭 구성
1. **기본정보**: 스타일번호·코드 분류(성별/제품종류/스타일 → 품명 자동)·이미지
2. **마카/요척/다운스펙**: 디자인 자체의 기술 자산 — 요척은 견적·작업지시가 가져다 씁니다
3. **샘플/견적/생산/자재발주/무역**: 이 디자인으로 진행된 거래 이력
4. **원가**: 롤업 (권한 필요)

### 참고
- 요척 기준표를 정확히 입력해 두면 견적 자재와 작업지시 요척이 자동 계산됩니다.
  `,
  'screens/material-closing': `
## 자재 월마감

지출결의(원화금액) 기준의 자재비 월 집계 — 6분류 요약·업체별·품목군별·추이.

### 사용법
- 마감월을 선택하면 해당 월 지결의 자재비가 분류별로 집계됩니다.
- 발주 연결된 지결 = 부자재 등 자동 분류, 그 외는 비용구분 키워드.

### 참고
- 이 화면은 **지급(지결) 기준**입니다. 발주 기준 매입 보고는
  '월 매출·매입' 화면 — 두 축의 차이가 "발주했지만 미지급" 금액입니다.
  `,
  'screens/approval-inbox': `
## 내 결재함

내가 결재할 문서(대기), 내가 올린 문서(상신), 완료·참조 문서를 관리합니다.

### 처리 방식
- **휴가·샘플요청·자금일보·자재발주**: 행 클릭 → 결재 다이얼로그에서 요약
  확인 후 바로 승인/반려 (자재발주는 승인 시 자동 '발주완료')
- **지출결의·견적 등**: 행 클릭 → 해당 문서 화면으로 이동해 처리

### 참고
- 합의(agreement) 단계가 있는 문서는 합의 현황이 표시됩니다.
- 반려 시 의견을 남기면 상신자에게 전달됩니다.
  `,
  'screens/expense-detail': `
## 지출결의서 상세 (조회)

지결의 금액·상태·집행 근거를 확인합니다. 수정은 [수정] 화면에서.

### 확인 포인트
- **집행 근거**: 상세 행의 근거 배지 — 입고 연결 / 작업지시 배분 / 직접 입력.
  근거 미연결 행은 스타일 원가에 집계되지 않습니다 (경고 배지).
- **증빙 첨부**: 세금계산서 등 사본 확인
- **상태 흐름**: 초안 → 제출 → 경리 접수 → 집행 중 → 완료

### 참고
- 인쇄는 전용 인쇄 화면을 사용하세요.
- 이 지결에 연결된 입고내역은 수정·삭제가 잠겨 있습니다 (정산 근거 보존).
  `,
  'screens/daily-cash-report': `
## 일일자금일보

일 단위 자금 현황(수입·지출·잔액)을 법인별로 관리합니다.

- 자금일보를 작성·제출하면 결재선을 타고, 승인 후 확정됩니다.
- 지출결의 집행·지급 계획과 연동되어 지급 내역이 반영됩니다.
- 상세 화면에서 계좌별 잔액·외화 잔액·항목별 수입/지출을 확인하세요.
  `,
  'screens/daily-cash-report-detail': `
## 자금일보 상세

- 계좌원장 기준의 계좌별 잔액과 항목별 수입·지출을 확인합니다.
- 외화 잔액은 통화 기준으로 표시됩니다.
- 결재는 내 결재함에서 다이얼로그로 바로 처리할 수 있습니다.

### 수입신용장(LC) 계좌 — 개설 = 차입 발생
- **개설완료된 LC의 액면 합계가 차입 잔액으로 자동 반영**됩니다 (일보 생성 시,
  계좌 통화 기준). 결제완료되면 자동으로 줄어듭니다 — 수기 입력이 필요 없습니다.
- 카드의 참고 라인에서 개설 기준 차입과 실제차입(송장 도착분)을 대사할 수 있고,
  [개설 기준 적용]으로 즉시 반영할 수 있습니다.
- LC가 자금일보에 안 잡히면: 해당 LC에 **한도계좌 연결** 여부를 확인하세요.
- 외화 차입 계좌는 마감잔액을 **외화로 입력**하면 평가환율로 원화 환산됩니다.
  `,
  'screens/payment-plan': `
## 자금계획

지급 예정 항목(지결·LC·정기 지급)을 모아 주간/월간 자금 계획을 세웁니다.

- 지출결의 접수분·LC 결제 예정·정기 지급 템플릿이 자동 반영됩니다.
- 계획 확정 후 지급 집행함에서 묶음 집행으로 처리합니다.

### LC 지급의 계좌 선택
- LC **상환**은 실제 돈이 나가는 지급이므로 출금계좌는 **보통예금·외화예금·현금**
  중에서 선택합니다 (외화 상환이면 외화예금 계좌를 자금기초자료에 등록하세요).
- 수입한도(LC 한도) 계좌는 출금 대상이 아닙니다 — 개설 시 차감된 한도는
  상환하면 자동 복원되고, 차입 잔액은 자금일보에 자동 반영됩니다.
- 개념: **개설완료(LC관리) = 차입 발생(돈 이동 없음) / 집행완료(자금계획) = 실제 출금**.
  `,
  'screens/finance-dashboard': `
## 자금 대시보드

자금 현황 요약 — 잔액 추이, 지급 예정, 여신(차입·LC) 한도를 한눈에.

- 카드에서 각 상세 화면(자금일보·자금계획·LC)으로 이동합니다.
  `,
  'screens/manual-payment': `
## 수동 지급 입력

지결 없이 나가는 지급(이체 수수료, 소액 등)을 직접 기록합니다.

- 지급일·계좌·금액·항목을 입력하면 자금일보에 반영됩니다.
- 반복 지급은 정기 지급 템플릿을 활용하세요.
  `,
  'screens/payment-execution': `
## 지급 집행함

접수된 지출결의·지급 계획을 실제 지급으로 집행합니다.

- 여러 건을 묶어 일괄 집행(묶음집행)할 수 있습니다.
- 집행 완료 시 지결 상태가 '집행 완료'로 전환되고 자금일보에 반영됩니다.
  `,
  'screens/trade-ledger': `
## 수출입 원장

수출(자재 수출·완제품 수입) 건별 통관·금액 흐름의 원장입니다.

- 수출신고·수입송장·LC와 연결되어 무역 흐름 전체를 추적합니다.
- 스타일(작업지시) 축으로 무역 원가가 원가비교에 집계됩니다.
  `,
  'screens/export-declaration': `
## 수출신고 관리

자재 수출(공장 발송분)의 수출신고를 관리합니다.

- 패킹리스트 기반으로 신고 건을 구성하고 신고번호·일자를 기록합니다.
- 부가세 영세율·관세 환급의 근거가 됩니다.
  `,
  'screens/vat-deferral': `
## 부가세유예 납부 관리

수입 부가세 납부유예 건의 납부 일정과 상태를 관리합니다.

- 수입 건별 유예 금액·납부 기한을 추적하고 납부 완료를 기록합니다.
  `,
  'screens/lc-repayment': `
## LC 상환 스케줄

LC(유산스 포함) 결제·상환 일정을 한눈에 봅니다.

- 만기일 기준으로 정렬되며 자금계획에 반영됩니다.
- 결제 완료 시 LC 상태와 연동됩니다.
- **✓확정(초록)** = 수입송장의 인수일 기반 만기. **추정(주황)** = 인수일 미입력 —
  ETA·송장일로 계산된 임시 만기이므로, 은행 인수 통보를 받으면
  수입송장에 인수일을 입력해 확정하세요. 자금 준비는 확정 만기 기준으로.
  `,
  'screens/material-master': `
## 자재 마스터

원부자재의 표준 코드·품명·규격을 관리하는 기준정보입니다.

- 발주·견적·입고에서 자동완성으로 참조되어 품명 표기가 통일됩니다.
- 등록 후보: 실무 입력에서 발견된 신규 자재를 검수해 승격하세요.
- 별칭 병합: 같은 자재의 다른 표기를 하나로 합칠 수 있습니다.
- 자재구분(원자재/부자재/포장재)이 지결 원가 자동 태깅의 기준이 됩니다.
  `,
  'screens/supplier-list': `
## 매입처 관리

협력업체(원단·부자재·임가공·물류) 마스터입니다.

- 카테고리(생산공장 등)·국가·계좌 정보를 관리합니다.
- **개인(프리랜서) 스위치**: 재단사·가봉사 등 사업자 없는 개인도 매입처로
  등록합니다 — 지결에서 원천징수(3.3%) 판정과 개인 지급 경고에 사용됩니다.
- 국가는 임가공(CMT) 생산국 자동 입력에 사용됩니다.
- 업체 정산·발주·원장에서 이 마스터를 참조합니다.
  `,
  'screens/design-code': `
## 디자인 코드 관리

성별(C04)·제품종류(C05)·스타일(C06) 코드 — 품명 자동 생성의 재료입니다.

- 품명 = "성별용 + 제품종류 + 스타일" (예: 여성용 다운점퍼 후드)
- **코드값은 등록 후 수정 불가** — 잘못 만든 코드는 사용여부 N으로 숨기고
  새로 추가하세요 (기존 디자인이 참조하기 때문).
- 엑셀 다운로드/업로드로 일괄 관리할 수 있습니다.
  `,
  'screens/production-dashboard': `
## 생산 대시보드 (작업현황)

진행 중인 메인오더 전체를 단계별 카드로 한눈에 봅니다.

- **단계 카드**: 요청접수 → 자재발주중 → 생산중 → 선적중 → 납품완료 단계별 건수
- 카드를 클릭하면 해당 단계의 메인오더 목록으로 이동합니다.
- **지연 경고**: 납기일이 임박하거나 지난 건은 강조 표시됩니다.
- 매일 아침 이 화면에서 전체 흐름을 점검하고, 이상 건은 상세 화면에서
  단계·일정을 갱신하세요.
  `,
  'screens/material-dashboard': `
## 자재 조달 현황

메인오더별 필요 자재 → 발주 → 입고 진행률을 추적합니다.

- **필요 자재**: 메인오더의 자재 목록(요척×수량)에서 산출된 소요량
- **발주/입고**: 자재 발주서와 입고 내역이 연결되어 충족률이 계산됩니다.
- 부족(미발주·미입고) 자재가 있는 스타일이 강조되므로, 생산 착수 전
  이 화면에서 자재 준비 상태를 확인하세요.
- 발주는 자재 발주 화면, 입고 처리는 입고 관리 화면에서 진행합니다.
  `,
  'screens/production-schedule': `
## 생산 스케줄

공장(라인)별 생산 일정을 간트 형태로 배정·조회합니다.

- 메인오더를 공장·기간에 배정하면 막대(bar)로 표시됩니다.
- 같은 공장의 기간 겹침을 한눈에 보고 부하를 조절할 수 있습니다.
- 납기일과 생산 종료일이 어긋나면 조정이 필요한 신호입니다.
- 공장 포털의 일일 실적 보고와 연계해 진척을 관리합니다.
  `,
  'screens/factory-portal': `
## 공장 포털

공장(협력사) 계정 전용 화면 — 배정된 작업의 확인과 현장 보고를 담당합니다.

- 공장 계정은 자기 공장에 배정된 작업지시만 볼 수 있습니다 (읽기 + 보고).
- **일일 실적 입력**: 재단/봉제/완성 수량 등 당일 실적을 입력하면
  본사 생산 현황에 실시간 반영됩니다.
- 문제 발생(자재 부족, 불량 등)은 보고 항목으로 남겨 본사와 공유하세요.
- 단가·원가 등 민감 정보는 이 화면에 표시되지 않습니다.
  `,
  'screens/expense-report': `
## 지출결의서 레포트

일별·기간별 지출 통계를 집계해 보는 관리자용 레포트입니다.

- 기간을 지정하면 일별 지출 합계와 건수, 비용구분별 분포를 확인할 수 있습니다.
- 지결 데이터 기준이므로, 결재·접수 상태에 따라 수치가 달라질 수 있습니다.
- 열람에는 별도 레포트 권한이 필요합니다.
- 월 단위 자금 흐름은 자금일보·월 매출·매입 보고 화면과 함께 보세요.
  `,
  'screens/notification-rules': `
## 알림 규칙 관리

멈춘 업무를 감지해 담당자에게 알리는 "다음 할 일" 엔진의 설정 화면입니다.

### 동작 원리
- 스캔이 규칙별로 데이터를 점검해 위반 건을 찾고, 담당자(문서 작성자)와
  수신 그룹에 알림(종 아이콘)을 보냅니다.
- 같은 건은 중복 알림하지 않고, 해소되면(예: 결재 처리) 자동 소멸합니다.
- 읽고도 해소하지 않으면 **재알림 간격** 후 다시 알립니다.
- 담당자는 알림에서 **보류**(사유 필수)를 걸어 의도된 지연을 마킹할 수 있습니다.

### 운영 순서 (중요)
1. 새 규칙은 **드라이런**으로 시작 — 알림 없이 감지 건수만 집계됩니다.
2. 1~2주 지켜보고 건수가 실무 감각과 맞으면 기준일수를 조정하세요.
3. 드라이런을 끄면 실제 발송이 시작됩니다.

### 참고
- 지연 감지(결재 정체 등)는 **영업일** 기준, 납기(D-N) 계열은 달력일 기준.
- 수신 그룹을 비우면 문서 담당자에게만 알립니다.
- 알림 본문에는 단가·원가 등 민감 정보가 포함되지 않습니다 (링크만).
  `,
  'screens/outsourcing-works': `
## 외주 작업

재단·봉제·나염·자수·와싱·가봉 등 **국내 외주 가공의 발주 기록**입니다.
"누구한테, 언제까지, 얼마에"를 남겨야 세금계산서가 왔을 때 대조할 근거가 생깁니다.

### 사용 흐름
1. 외주를 맡길 때 [외주 등록] — 공정·외주처(업체 또는 👤 개인)·수량·단가·납기 입력
2. 작업이 끝나면 [완료] 처리
3. 세금계산서·청구서가 오면 **업체 정산** 화면에서 대조 → 지출결의서 생성
   (생성 시 이 기록에 지결번호가 표시되고 정산 필드가 잠깁니다)

### 참고
- 본 생산 외주는 메인오더를 연결하세요 — 지결 배분 시 그 스타일의 가공비로 집계됩니다.
- 👤 개인(프리랜서)에게 지급하는 건은 지결에서 원천징수(3.3%)를 선택할 수 있습니다.
- 정산된 건은 외주처·납기·수량·단가가 잠기고, 완료 처리·메모는 계속 수정 가능합니다.
- 그레이딩은 메인오더 상세의 그레이딩 탭에서 별도 관리됩니다.
  `,
  'screens/integrity-checks': `
## 데이터 정합성 점검

깨진 데이터(LC 한도계좌 미연결, 평가환율 미입력 등)를 자동으로 찾아
관리자가 담당자에게 정리를 요청할 수 있게 하는 화면입니다.

### 사용법
1. [지금 점검] — 전 항목을 즉시 검사합니다 (조회 전용, 데이터 변경 없음)
2. 이상 항목이 자동으로 펼쳐집니다 — [바로가기]로 해당 화면에서 정리
3. 매일 알림 스캔에도 포함되어, 새 이상이 생기면 관리자에게 알림이 옵니다

### 심각도
- **즉시 조치**: 금액·한도 계산이 틀어지는 것 (LC 미연결, 환율 미입력, 한도 초과)
- **정리 필요**: 원가 집계 누락 원인 (견적 미연결, 근거 없는 지결, 스타일 미연결)
- **참고**: 확인만 필요한 것 (추정 만기, 개인 과세 지결)

### 확장
새 점검 항목은 서버 core/integrity.py에 함수 하나 등록으로 추가됩니다 —
시스템이 진화하며 새 규칙이 생기면 여기에 계속 쌓입니다.
  `,
  'screens/employment-terms': `
## 근로조건 관리

직원별 **급여 구성과 고정연장시간**을 적용일 기준 이력으로 관리하는 기준정보입니다. 근로계약서를 만들 때
각 직원의 현재 조건이 자동으로 들어가므로, 담당자는 계약서 화면에서 값을 입력하지 않습니다.

### 처음 채우기
1. [급여대장 엑셀 내려받기] — 활성 직원 전원이 행으로 나옵니다(조건 없는 직원은 빈 행)
2. 지금 쓰는 급여대장의 값을 열에 맞춰 붙여 넣고(적용일·기본급·고정연장근로수당·식대·차량유지비·기타·육아·고정연장시간·취업직종·직책) [엑셀 올리기]
3. 검증 결과를 확인하고 오류 0건이면 [반영]. 월지급액은 비워 두면 구성 합계로 계산됩니다

### 이후 운영
- 연봉이 바뀌면 그 직원의 [새 적용일 등록] — 이전 조건은 이력으로 남습니다. 계약서는 만드는 시점의 최신 조건을 씁니다
- 통상시급 = 월지급액 ÷ (209 + 고정연장시간). 최저임금 환산시급 = (기본급+식대+차량유지비+육아수당) ÷ 209 이며,
  [최저시급 기준]에 연도별 최저시급을 넣어 두면 미달자에게 경고가 뜹니다
- 여기 값을 바꿔도 **이미 만들어진 계약서는 변하지 않습니다**. 계약서를 다시 만들려면 계약서 상세에서 [근로조건 마스터에서 다시 가져오기]

### 주의
- 급여는 민감 정보입니다. 이 화면과 엑셀은 인사담당자·계약관리자만 접근하며 열람이 기록됩니다
- 직원 주소·연락처·입사일은 직원 관리에 있으며 계약서 제1조에 자동으로 들어갑니다
  `,
  'screens/contract-list': `
## 계약서 관리

직원 대상 계약서를 시스템으로 배포하고, **열람 확인 + 전자서명 동의**를 법적 증거(IP·시간·파일 해시)와 함께 남기는 전자계약 화면입니다.
현재 회사는 **근로계약서**(전 직원, 매년 갱신)와 **영업비밀보호서약서**(디자인실 등 대상 부서) 두 가지를 씁니다. 분류 목록에도 이 둘만 보입니다.

### 핵심 개념 — 양식과 계약서는 다릅니다
- **양식(양식 관리 탭)**: 문서의 **본문 서식 + 조건 항목 + 동의 규칙**입니다. 분류를 고르고 [표준 서식 불러오기]를 누르면 회사 서식이 채워집니다.
  한 번 만들어 두고 반복 사용합니다. **배포된 계약서가 있는 양식은 잠겨서 내용(본문·조건·동의 문구·서식 파일)을 고칠 수 없습니다** — 직원이 읽고 동의한 문서와 서식이 달라지면 안 되기 때문입니다. 고치려면 [새 버전]을 누르세요. 내용이 복사된 다음 판이 만들어지고 이전 판은 비활성되며, 이미 배포된 계약서는 이전 판을 그대로 유지합니다. 이름·설명·사용 여부는 잠긴 뒤에도 바꿀 수 있습니다.
- **계약서(계약서 탭)**: 실제로 직원에게 배포되는 건입니다. 양식과 대상자만 고르면 직원별 PDF가 자동으로 만들어집니다.

### 배포 절차
1. **양식 관리 탭**에서 양식이 없으면 먼저 등록 — 분류 선택 → [표준 서식 불러오기] → [미리보기]로 확인 → 등록
2. **계약서 탭 → 계약서 생성**: 양식 선택, 계약서명(예: "2027년 근로계약서"), 확인기한, 독촉알림(기한 N일 전), 대상 선택
   - 근로계약서: 보통 **전직원**. **계약 시작일**을 넣으면 근로조건 관리(마스터)에서 그 날짜에 적용 중인 조건(적용일이 시작일 이전인 것 중 최신)이 자동으로 들어갑니다. 2026년 계약이면 2026-01-01, 내년 계약을 미리 만들면 2027-01-01
   - 영업비밀보호서약서: **부서별**(디자인실) 또는 개별. 조건 입력 없이 이름·소속·날짜만 자동
3. 생성과 동시에 대상자별 PDF가 만들어집니다. 상세 화면에서 문서와 [직원 화면 미리보기]를 확인한 뒤 **배포하기** → 대상 직원에게 알림 발송
4. 직원은 "내 계약서" 메뉴에서 열람 → 서명 → 동의. 진행 현황은 상세 화면에서 실시간 확인
5. 동의가 끝나면 서명란에 회사 직인과 직원 서명이 들어간 **서명 완성본 PDF**가 만들어져 양쪽 모두 내려받을 수 있습니다

### 문서는 어떻게 만들어지나
- **근로계약서**: 양식의 조건 항목(계약 시작일·직책·급여 구성·고정연장시간·수습 등)이 **근로조건 관리**(마스터)와 연결되어 있어,
  계약서를 만들면 각 직원의 현재 조건이 자동으로 채워지고 회사 서식으로 PDF까지 만들어집니다.
  마스터에 조건이 없는 직원은 문서가 안 만들어지니 근로조건 관리에서 먼저 등록한 뒤 상세 화면의 [근로조건 마스터에서 다시 가져오기]를 누르세요.
- **영업비밀보호서약서**: 조건 항목이 없습니다. 회사명·서약자 이름·소속·날짜만 자동으로 들어가므로 대상자만 고르면 끝입니다.
- 특정 직원만 값을 달리해야 하면 상세 화면 "계약 조건" [입력] 또는 [조건 엑셀 내려받기 → 채워서 올리기]로 고칩니다. 엑셀은 먼저 검증만 하고 오류가 없을 때 [반영]. 이미 동의한 직원은 건너뜁니다.
- 조건 값은 직원 동의 화면 상단 "계약 조건" 표에 보이고, 동의 시 그대로 기록되며 서명 완성본에도 남습니다.
- 양식 관리 ⑤에서 조건 항목, ⑥에서 본문 서식을 관리합니다. 문구를 고쳐야 하면 ⑥에서 수정 → [미리보기]로 확인. 배포된 양식이면 [새 버전]으로.

### 대상 선택
- **전직원 / 부서별 / 개별 선택** 세 가지. 어느 방식이든 생성 즉시 대상자 목록과 문서가 만들어집니다.
- 전직원·부서별은 **배포 시점**에 그 사이 입사한 직원이 자동으로 추가됩니다.
- 매년 근로계약 갱신: 근로조건 관리에 새 적용일(예: 2027-01-01)로 조건을 올린 뒤 → "2027년 근로계약서"를 계약 시작일 2027-01-01로 전직원 생성 → 배포. 올해 조건과 내년 조건이 둘 다 등록돼 있어도 계약 시작일로 구분됩니다.

### 상태 흐름
**작성중** → (배포하기) → **배포됨** → (첫 열람) → **진행중** → (전원 동의) → **완료**
- **기한만료**: 확인기한이 지나면 매일 자동 처리, 미동의자는 "기한만료"로 표시
- **취소**: 작성중 상태에서만 가능. 취소된 건은 되살릴 수 없으니 **새로 생성**하세요
- 배포 후에는 대상자·기한을 바꿀 수 없습니다. 잘못 배포했으면 만료를 기다리거나 관리자에게 문의

### 자주 하는 질문
- 상세 화면에 "문서가 아직 없다"고 나와요 → 근로계약서면 그 직원의 근로조건이 마스터에 없는 경우입니다. 등록 후 [근로조건 마스터에서 다시 가져오기]. 서약서면 [문서 다시 생성 (전원)].
- 양식 내용을 고치고 싶은데 잠겨 있어요 → 배포된 계약서가 있어서입니다. [새 버전]을 만들어 고치고, 다음 계약서부터 새 버전을 쓰세요.
- 양식을 삭제하고 싶어요 → 계약서가 연결된 양식은 삭제할 수 없습니다. [비활성]으로 계약서 생성 목록에서 빼세요.
- 필터의 "분류"는 양식의 분류입니다.

### 권한
- **계약관리자**: 양식·계약서 생성/배포/독촉/취소, 조건 입력, 문서 재생성
- **계약조회자**: 현황 조회, Excel 다운로드
- 근로조건 관리(급여)는 **인사담당자·계약관리자**만 볼 수 있습니다
  `,
  'screens/contract-detail': `
## 계약서 상세

한 건의 계약서에 대한 정보·진행 현황·대상자별 상태를 보고, 문서 확인 → 배포 → 독촉 → 완성본 관리까지 처리합니다.
맨 위 흐름 단계(준비 → 배포 → 열람·동의 → 완료)에 지금 누가 무엇을 해야 하는지 표시됩니다.

### 상단 버튼 (상태에 따라 달라짐)
- **직원 화면 미리보기** (작성중): 직원이 보게 될 화면(문서·동의 문구·서명란)을 배포 전에 그대로 확인합니다. 배포 전에 꼭 한 번 보세요.
- **배포하기** (작성중): 대상자에게 알림을 보내고 상태를 "배포됨"으로 바꿉니다. 문서가 없는 대상자가 있으면 경고합니다.
  **배포 후에는 대상자·기한·문서를 수정할 수 없습니다.**
- **독촉 발송** (배포됨/진행중): 미확인·열람중 직원에게 즉시 알림. 자동 독촉과 별개로 수동 발송
- **Excel 다운로드** (배포 후): 대상자별 열람·동의 기록을 내려받습니다 (증빙용)
- **취소** (작성중): 사유를 적어 취소. 되살릴 수 없으므로 신중히

### 진행 현황
- 동의 인원 / 총 대상자, 상태별 인원(미확인·열람중·동의·거부·만료)
- 전원 동의 시 계약서 상태가 자동으로 **완료**로 바뀝니다

### 대상자 목록 (작성중일 때)
- **계약 조건** 열(근로계약서): 마스터에서 가져온 값이 보이고, 빠진 항목은 빨간 글씨로 표시됩니다. [입력]으로 개별 수정.
- 목록 위 도구: [조건 엑셀 내려받기/올리기], [근로조건 마스터에서 다시 가져오기](마스터 값으로 덮어씀), [계약서 문서 생성]. 서약서처럼 조건이 없는 양식은 [문서 다시 생성 (전원)]만 있습니다.
- **개별파일** 열: 자동 생성된 PDF를 열어 볼 수 있습니다. 자동 생성 양식에서는 손으로 올리거나 교체할 수 없습니다 — 조건을 고치면 문서가 다시 만들어지므로 손으로 바꾼 파일은 조건과 어긋나기 때문입니다.

### 대상자 목록 (배포 후)
- **최초열람 / 열람시간 / 동의일시**: 직원이 실제로 문서를 열고 읽었다는 기록
- **서명 완성본**: 동의 완료 행에서 내려받기. 서명란 자리에 회사 직인과 직원 자필 서명이 들어가고 마지막 장 하단에 전자서명 정보(동의 시각·IP·문서 해시) 상자가 붙습니다. 분쟁·감사 대응 증빙은 이 파일입니다.
- **거부**: 사유가 행에 표시되고 담당자에게 알림이 갑니다. 조건이 잘못됐으면 근로조건 관리에서 고친 뒤 새 계약서로 배포하고, 직원 오해였으면 [재요청]으로 그 직원만 처음부터 다시 읽고 서명하게 합니다.
- **독촉**: 자동+수동 독촉 발송 횟수
- 상태 필터로 미확인자만 추려 볼 수 있습니다

### 동의가 안 된다고 문의가 오면
- 양식의 **전체열람 필수**가 켜져 있으면 직원이 문서를 끝까지 내리고 **"전체 확인 완료"** 버튼을 눌러야 합니다
- **최소열람시간**(초)을 채워야 동의 버튼이 활성화됩니다 (화면에 남은 시간이 표시됨)
- **서명 필수** 양식은 서명란에 자필 서명을 그려야 동의 버튼이 켜집니다
- 완성본 PDF가 "준비 중"이면 잠시 후 다시 내려받으세요. 계속 안 되면 시스템 담당자에게 문의
  `,
  'screens/my-contract': `
## 내 계약서

회사가 나에게 배포한 계약서를 열람하고 동의하는 화면입니다.

### 절차
1. **미확인** 탭에서 계약서를 클릭
2. 화면 안에 계약서가 바로 보입니다. **끝까지 읽습니다** — 근로계약서는 위쪽 "계약 조건" 표(계약 시작일·급여 구성 등)도 확인하세요.
   열람 시간이 표시되며, 양식에 정한 **최소 열람시간**을 채워야 동의할 수 있습니다
3. 문서를 끝까지 봤으면 **"전체 확인 완료"** 버튼을 누릅니다 (전체열람 필수 양식일 때)
4. 동의 항목 체크 → **서명란에 자필 서명**(마우스·손가락) → **동의합니다**. 동의는 **철회할 수 없으니** 내용을 충분히 확인하세요
5. 내용이 사실과 다르면 **거부**를 누르고 사유를 적습니다 — 담당자에게 전달되며, 확인 후 다시 요청받을 수 있습니다
6. 동의가 끝나면 서명란에 회사 직인과 내 서명이 들어간 **서명 완성본 PDF**를 내려받아 보관합니다 (같은 화면과 "완료" 탭에서 언제든 다시 내려받기 가능)

### 알아두기
- 동의 시각·접속 정보·문서 해시가 함께 저장되어 서면 서명과 같은 효력의 기록이 됩니다
- **확인기한**이 지나면 동의할 수 없고 "기한만료"로 처리됩니다. 기한 전 알림이 오면 미루지 마세요
- 계약서가 화면에 안 보이면 [새 탭에서 열기]로 확인하거나 담당자에게 문의하세요
- 이 화면은 내 업무(계약서 확인 항목)에서도 바로 들어올 수 있습니다
  `,
  'screens/default': `
## 도움말

이 화면에 대한 도움말이 아직 작성되지 않았습니다.

문의사항이 있으시면 관리자에게 연락해주세요.
  `,
  'screens/expense-list': `
## 지출결의서

모든 비용은 지출결의서로 집행됩니다. 발주·입고 근거와 세금계산서를 대조해
작성하고, 결재 후 경리과가 접수·집행합니다.

### 작성 흐름
1. **[업체 정산]** (권장): 업체와 마감월을 고르면 미정산 내역 5원천(자재 입고·
   샘플 외주·그레이딩·외주작업·수입 부대비)이 모입니다. 세금계산서 금액과
   대조해 선택 → 지결 초안 자동 생성.
2. 또는 [신규 작성]에서 직접 입력 — 상세 행에 입고내역/작업지시 배분을 연결하세요.
   연결해야 스타일 원가에 집계됩니다.

### 저장 시 자동 크로스체크
- **차단(오류)**: 다른 지결에 이미 연결된 입고 = 동일 비용 중복 지출
- **경고**: 동일 거래처·금액 유사 지결(60일), 입고수량 초과, 수입송장 이중계상,
  견적 예산 초과(항목별 현황표), 근거 없는 원가성 지결, 과세 지결 증빙 미첨부

### 증빙 첨부
저장 후 '증빙 첨부' 카드에서 세금계산서·거래명세표 등 사본을 유형별로 첨부하세요.
종이 원본 보관 관행은 그대로, 시스템에는 사본을 남깁니다.

### 개인(프리랜서) 지급
재단사·가봉사 등 개인 거래처는 세금 구분에서 **원천징수(3.3%)** 를 선택하면
공제·실지급액이 자동 계산됩니다 (회사가 떼고 신고하는 건만 — 세무사 별도
처리 건은 기존 구분 유지). 개인 거래처에 '과세'를 고르면 확인 경고가 뜹니다.

### 정산 후 불변 규칙
지결에 연결된 입고내역, 지결번호가 기입된 샘플 원장·그레이딩은 수정·삭제가
잠깁니다. 정정은 해당 지결에서 연결을 해제하거나 정정 지결로 처리하세요.
  `,
  'screens/vendor-settlement': `
## 업체 정산

세금계산서가 도착하면 이 화면에서 발주 내역과 대조해 지출결의서를 만듭니다.

### 사용 순서
1. 업체 선택 (+ 필요 시 월 필터)
2. 미정산 내역 확인 — **5원천**: 자재 입고 / 샘플 외주(원장) / 그레이딩 /
   외주 작업(재단·봉제·나염 등) / 수입 부대비(관세사·포워더 송장 단위)
3. 세금계산서 금액 입력 → 선택 합계와 자동 대조 (차이 표시)
4. 청구분에 해당하는 항목 체크 → [지출결의서 생성]
5. 생성된 지결에서 내용 확인 → 세금계산서 첨부 → 크로스체크 → 제출

### 참고
- 이미 지결에 연결·정산된 항목은 목록에 나오지 않습니다 (지결 취소 시 자동 복귀).
- 샘플 외주·수입 부대비는 **참고 기표**로 생성됩니다 — 원가는 원장·수입송장이
  직접 집계하므로 지결로 이중집계되지 않습니다.
- 개인(프리랜서) 업체는 지결에서 원천징수(3.3%)를 선택할 수 있습니다.
- 운영 권장: **원가성 지출은 전부 이 화면에서 시작** — 직접 지결 작성은
  급여·경비 등 비원가성만.
  `,
  'screens/monthly-trade': `
## 월 매출·매입 보고

자재과·구매부의 월 보고: 매출 = 본사 거래명세표, 매입 = 발주서.

### 매출 (거래명세표)
- 본사에서 거래명세표가 오면 [거래명세표 등록]으로 입력합니다.
- **매출 인식월 = 명세일의 월** (자동 지정)
- 메인오더를 연결하면 시스템 기대 단가(합의가 > 확정단가)와 자동 대조되어
  차이가 Δ로 표시됩니다. 명세표 사본을 첨부해 보관하세요.

### 매입 (발주서)
- 발주일 기준, 취소 제외 발주서의 품목 합계입니다.
- 지급(지결) 기준 집계는 자재 월마감 화면과 별개의 축입니다 —
  "발주했지만 아직 지급 전"인 금액의 차이를 볼 수 있습니다.
  `,
  'screens/main-request-list': `
## 메인오더 목록

메인오더은 고객사로부터 받은 본 생산 요청을 관리하는 핵심 단위입니다.

### 화면 구성
- **필터 영역**: 브랜드, 상태, 납기일로 검색
- **목록 테이블**: 모든 메인오더 표시
- **D-Day**: 납기일까지 남은 일수 (지연 시 빨간색)

### 주요 기능
1. **신규 요청**: 새로운 메인오더 생성 (메인오더관리자 권한 필요)
2. **상세 보기**: 행 클릭 시 통합 대시보드로 이동
3. **원가 정보**: 원가조회자 이상 권한 필요

### 상태 흐름
\`요청접수\` → \`자재발주중\` → \`생산중\` → \`선적중\` → \`납품완료\` → \`정산완료\`
  `,
  'screens/main-request-detail': `
## 메인오더 상세 (통합 대시보드)

메인오더의 모든 정보를 한 곳에서 확인하고 관리할 수 있습니다.

### 요약 카드
- **상태**: 현재 진행 상태
- **납기일**: 최종 납품 예정일 및 D-Day
- **발주수량**: 총 발주 수량
- **총 원가**: 누적 원가 (권한 필요)
- **단가**: 개당 원가 (권한 필요)
- **브랜드**: 고객사 브랜드

### 탭 메뉴 (2026-07 개편)
1. **개요**: 스타일·일정·기본 정보
2. **생산지시서**: 공장 전달 문서를 이 탭에서 조회·편집·엑셀 출력.
   없으면 [생산지시서 생성] — 브랜드·디자인·수량·공장·혼용율·자재·수량배분을
   메인오더에서 자동 승계합니다. 치수 스펙(제시/QC/패턴/편차)·수정지시·도식화만
   입력하면 실무 양식 그대로 출력됩니다.
3. **수량배분**: 색상×사이즈 수량 관리
4. **자재현황**: 필요 자재·발주·입고 추적
5. **임가공계약 / 그레이딩 / 요척**: 그레이딩은 외주업체·납기·예상비용 기록,
   요척은 [디자인 요척에서 가져오기]로 기준표×생산수량 자동 계산
6. **이슈 / 마일스톤 / 현장보고 / 선적·LC / 원가분석 / 참여자**

### 권한별 표시
- 원가 정보: "원가조회자" 이상 권한 필요
- 수정 버튼: "메인오더관리자" 권한 필요
- 마일스톤 완료: "생산관리자" 권한 필요
  `,
  'screens/material-lot-list': `
## 자재 LOT 관리

고가의 벌크 자재(다운 등)를 LOT 단위로 구매하여 스타일별로 배분 관리합니다.

### 화면 구성
- **필터**: 자재유형, 상태, 잔여수량 유무
- **사용률**: 배분된 수량 비율 표시
- **배분 버튼**: 잔여수량이 있는 LOT에 표시

### 주요 기능
1. **신규 LOT**: 새로운 LOT 등록
2. **배분**: 특정 메인오더에 수량 배분
3. **입고 처리**: 발주한 LOT 입고 확인

### 상태 흐름
\`발주\` → \`입고\` → \`배분완료\` → \`소진\`

### 원가 계산
배분 시 단가 × 수량이 해당 메인오더의 원가에 자동 반영됩니다.
  `,
  'screens/cost-category': `
## 원가 카테고리 관리

원가 항목을 체계적으로 분류하기 위한 카테고리 트리를 관리합니다.

### 카테고리 유형
- **자재비**: 원부자재 구매 비용
- **인건비**: 생산 인건비
- **경비**: 기타 경비
- **외주비**: 외부 가공비
- **물류비**: 운송/통관 비용
- **기타**: 분류 외 비용

### 주요 기능
1. **트리 구조**: 상위/하위 카테고리 계층 관리
2. **하위 추가**: 기존 카테고리 아래 새 카테고리 생성
3. **정렬순서**: 표시 순서 지정
  `,
  'screens/lc-list': `
## LC(신용장) 관리

수출입 거래를 위한 신용장(Letter of Credit)을 관리합니다.

### 화면 구성
- **상태 필터**: LC 진행 상태별 조회
- **만료임박 필터**: N일 이내 만료 예정 LC 조회
- **D-Day**: 유효기일까지 남은 일수

### LC 유형
- **At Sight**: 일람불 신용장 (즉시 결제)
- **Usance**: 기한부 신용장 (N일 후 결제)

### 상태 흐름
\`초안\` → \`개설요청\` → \`개설완료\` → \`선적완료\` → \`매입완료\` → \`결제완료\`

### 조건변경 (Amendment)
개설 완료 후 조건을 변경해야 할 경우 조건변경 내역을 추가합니다.
  `,
  'screens/lc-detail': `
## LC 상세

신용장의 모든 정보를 확인하고 상태를 변경할 수 있습니다.

### 기본 정보
- LC번호, 유형, 메인오더 연결
- 금액 및 과부족 허용 범위 (최대/최소 금액 자동 계산)

### 당사자 정보
- 개설은행, 통지은행, 매입은행
- 개설의뢰인 (Applicant), 수익자 (Beneficiary)

### 선적/납기 정보
- 선적항, 양륙항
- 분할선적/환적 허용 여부
- 최종선적일 및 D-Day

### 상태 변경 (LC관리자 권한 필요)
- 개설 요청 / 개설 완료
- 선적 완료 / 매입 완료 / 결제 완료
  `,
  'screens/shipment-list': `
## 선적 스케줄 관리

메인오더의 선적 일정을 관리합니다.

### 화면 구성
- **상태 필터**: 선적 진행 상태별 조회
- **운송중만**: 현재 운송 중인 선적만 표시
- **지연만**: 지연된 선적만 표시

### 선적 유형
- **SEA**: 해상 운송
- **AIR**: 항공 운송

### 상태 흐름
\`예정\` → \`부킹완료\` → \`선적완료\` → \`운송중\` → \`도착\` → \`인도완료\`

### 주요 정보
- **ETD**: 예상 출항일 (Estimated Time of Departure)
- **ETA**: 예상 도착일 (Estimated Time of Arrival)
- **B/L번호**: 선하증권 번호
  `,
  'workflow/overview': `
## 업무 흐름 개요

의류 에이전트의 전체 업무 프로세스입니다.
메뉴는 물건의 위치를 따라 **디자인·견적 → 자재 → 생산 → 제품 → 무역** 순서입니다 (2026-08 재편).

### 메뉴 구성 (물건 위치 기준)

| 메뉴 | 기준 | 주요 항목 |
|------|------|----------|
| 디자인·견적 | 수주 전~오더 관리 | 디자인, 샘플, 견적, 시즌 현황, **스케줄 보드** |
| 자재 | 자재가 공장에 도착할 때까지 | 발주서, 입고(자재), 자재전달, 자재 패킹리스트, 출고, 선적 스케줄 |
| 생산 | 공장 안 | 메인오더, 작업현황, 검품, 생산지시서, 생산 스케줄(공장 간트) |
| **제품** | 완제품이 한국에 도착한 이후 | **완제품 패킹리스트, 완제품 입고, 납품/반품** |
| 무역 | 수출입 서류 | LC, 수입송장, 수출신고, 수출입 원장 |

### 1. 샘플 단계 (디자인·견적)
\`샘플요청\` → \`디자인개발\` → \`견적제출\` → \`승인\`

### 2. 본생산 단계
\`메인오더접수\` → \`자재발주\`(자재) → \`LC개설\`(무역) → \`생산\`(생산) → \`완제품 입고·납품\`(제품)

- 진행 상황은 **디자인·견적 > 스케줄 보드**에서 스타일×단계로 한눈에 확인합니다.
- 용어: **메인오더** = 오더 관리 문서(구 작업지시서), **생산지시서** = 공장에 보내는 제작 문서.

### 3. 정산 단계
\`원가집계\` → \`매입\` → \`결제\` → \`정산완료\`

---

### 부서별 역할

| 부서 | 주요 업무 |
|------|----------|
| MD | 고객 커뮤니케이션, 메인오더 관리 |
| 자재팀 | 원부자재 발주/입고 관리 |
| 생산팀 | 생산 스케줄/마일스톤 관리 |
| 무역팀 | LC 개설, 선적 관리 |
| QC | 검품 일정 관리 |

---

### 원가 관리 포인트

1. **지출결의서**: 모든 비용은 지출결의서를 통해 발생
2. **LOT 배분**: 고가 벌크 자재는 LOT 단위로 관리
3. **자동 집계**: 메인오더 단위로 원가 자동 집계
  `,
  'screens/sample-request-list': `
## 샘플요청 목록

브랜드로부터 받은 샘플 제작 요청을 관리합니다.

### 화면 구성
- **필터 영역**: 브랜드, 상태, 샘플유형, 납기일로 검색
- **탭 메뉴**: 전체 / 승인대기 / 작업중 / 납기초과
- **목록 테이블**: 모든 샘플요청 표시
- **회차 표시**: 현재 진행 중인 회차 번호
- **D-Day**: 납기일까지 남은 일수 (지연 시 빨간색)

### 주요 기능
1. **신규 요청**: 새로운 샘플요청 생성 (샘플관리자 권한 필요)
2. **상세 보기**: 행 클릭 시 상세 화면으로 이동
3. **상태 관리**: 다단계 승인 및 작업 진행 상황 확인

### 샘플 유형
- **프로토 (초기 개발)**: 디자인 컨셉 확인을 위한 최초 샘플. 기본적인 형태와 실루엣 확인
- **세일즈맨 (영업용)**: 영업/바이어 미팅용 샘플. 고객 반응 확인 목적
- **PP샘플 (생산전 확인)**: Pre-Production 샘플. 본생산 전 최종 품질/디테일 확인
- **TOP샘플 (최종 확인)**: Top of Production 샘플. 생산 초기 라인에서 뽑은 최종 확인용
- **AD샘플 (광고/촬영용)**: 광고, 카탈로그, 촬영 등에 사용되는 샘플

### 상태 흐름
\`요청\` → \`승인대기\` → \`승인완료\` → \`작업중\` → \`완료\`

### 다단계 승인
샘플유형에 따라 승인 단계가 다릅니다:
- **프로토/세일즈맨/기타**: 팀장 → 부서장 (2단계)
- **PP/TOP/AD**: 팀장 → 부서장 → 사장 (3단계)
  `,
  'screens/sample-request-detail': `
## 샘플요청 상세

샘플요청의 모든 정보를 확인하고 관리합니다.

### 요약 카드
- **브랜드**: 요청 브랜드
- **디자인**: 대상 디자인 (스타일번호)
- **샘플유형**: 프로토/세일즈맨/PP/TOP/AD/기타
- **수량**: 샘플 수량
- **납기일**: 납품 예정일 및 D-Day
- **실제비용**: 누적 비용 (전체 회차 합산)

### 탭 메뉴
1. **기본 정보**: 요청 상세 정보
2. **승인 Flow**: 다단계 승인 진행 현황 (N단계 체인)
3. **회차 관리**: 회차별 타임라인 + 회차 상세
4. **작업 내역**: 레거시 데이터 (회차 없는 기존 작업)
5. **자재 내역**: 레거시 데이터 (회차 없는 기존 자재)

### 승인 Flow (다단계 승인)
- 샘플유형에 따라 승인 체인이 자동 구성됩니다
- 시스템이 조직도 기반으로 승인자를 자동 추천합니다
- 요청자가 승인자를 수정할 수 있습니다
- 순차적으로 승인이 진행되며, 이전 단계 미승인 시 다음 단계 불가
- 반려 시 후속 단계가 자동 무효화됩니다

### 회차 관리 (라운드)
샘플은 여러 회차로 반복 제작될 수 있습니다:
1. 승인 완료 시 **1차 회차**가 자동 생성됩니다
2. 각 회차에서 작업단계(패턴→재단→봉제→...)를 진행합니다
3. 모든 작업 완료 후 **검토 요청**을 합니다
4. 검토자가 **승인/수정필요/반려**를 결정합니다
5. 수정필요 시 **다음 회차가 자동 생성**됩니다
6. 누적 비용이 50만원 초과 시 추가 승인이 필요합니다

### 작업 유형
- 패턴, 재단, 봉제, 마무리, 다운배분, 그레이딩, 외주제작, 기타

### 자재 유형
- 원단, 부자재, 다운, 안감, 기타

### 담당자(자사) 안내
- 이 필드는 **우리 회사의 담당 직원**을 지정합니다
- 해당 샘플요청을 주관하는 사내 담당자를 선택하세요
- 드롭다운에서 직원 이름과 소속 부서가 함께 표시됩니다

### 권한별 기능
- **샘플관리자**: 요청 생성/수정, 작업/자재 추가, 회차 시작/검토요청
- **샘플승인자**: 다단계 승인, 반려, 회차 검토 결과 입력
  `
}

const renderedContent = computed(() => {
  // 간단한 마크다운 렌더링 (실제로는 marked.js 등 사용 권장)
  let html = content.value
    // 헤딩
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // 굵은 글씨
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // 코드
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // 수평선
    .replace(/^---$/gm, '<hr>')
    // 테이블 (간단한 처리)
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim())
      if (cells.some(c => c.trim() === '------' || c.trim().match(/^-+$/))) {
        return ''
      }
      const isHeader = content.value.indexOf(match) < content.value.indexOf('|---')
      const tag = isHeader ? 'th' : 'td'
      return `<tr>${cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('')}</tr>`
    })
    // 리스트
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    // 줄바꿈
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')

  // 테이블 래핑
  if (html.includes('<tr>')) {
    html = html.replace(/(<tr>[\s\S]*?<\/tr>)+/g, '<table class="help-table">$&</table>')
  }

  // 리스트 래핑
  html = html.replace(/(<li>[\s\S]*?<\/li>)+/g, '<ul>$&</ul>')

  return `<p>${html}</p>`
})

const loadContent = () => {
  loading.value = true
  error.value = false

  // 내장 콘텐츠 로드 (실제로는 서버에서 가져올 수 있음)
  setTimeout(() => {
    content.value = helpContents[props.contentPath] || helpContents['screens/default']
    loading.value = false
  }, 100)
}

watch(() => props.contentPath, () => {
  loadContent()
})

onMounted(() => {
  loadContent()
})
</script>

<style scoped>
.help-content {
  padding: 10px;
  font-size: 14px;
  line-height: 1.6;
}

.markdown-content :deep(h1) {
  font-size: 20px;
  margin: 20px 0 10px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 5px;
}

.markdown-content :deep(h2) {
  font-size: 18px;
  margin: 15px 0 10px;
  color: #409eff;
}

.markdown-content :deep(h3) {
  font-size: 16px;
  margin: 12px 0 8px;
}

.markdown-content :deep(code) {
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
  color: #e6a23c;
}

.markdown-content :deep(strong) {
  color: #303133;
}

.markdown-content :deep(ul) {
  padding-left: 20px;
  margin: 10px 0;
}

.markdown-content :deep(li) {
  margin: 5px 0;
}

.markdown-content :deep(hr) {
  border: none;
  border-top: 1px solid #ebeef5;
  margin: 15px 0;
}

.markdown-content :deep(.help-table) {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.markdown-content :deep(.help-table th),
.markdown-content :deep(.help-table td) {
  border: 1px solid #ebeef5;
  padding: 8px;
  text-align: left;
}

.markdown-content :deep(.help-table th) {
  background-color: #f5f7fa;
  font-weight: 600;
}

.error-message {
  text-align: center;
  padding: 40px;
}
</style>
